import React, { useState, useEffect } from 'react';
import { Search, Calendar, TrendingUp, X } from 'lucide-react';
import useEmployees from '../../hooks/useEmployees';
import VariableIncomesModule from './VariableIncomesModule/VariableIncomesModule';

const fmt = (n) =>
  parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 });

export default function IngresosVariablesModule({ deepLinkEmployeeId, deepLinkPeriodo, deepLinkQuincena, onDeepLinkConsumed }) {
  const { employees, loading } = useEmployees();
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedPeriodo, setSelectedPeriodo]   = useState(new Date().toISOString().slice(0, 7));
  const [selectedQuincena, setSelectedQuincena] = useState('Q1');
  const [searchTerm, setSearchTerm]             = useState('');

  // Deep-link: auto-seleccionar empleado al navegar desde historial de aprobaciones
  useEffect(() => {
    if (!deepLinkEmployeeId || loading || employees.length === 0) return;
    const emp = employees.find(e => e.id === deepLinkEmployeeId);
    if (emp) {
      if (deepLinkPeriodo)  setSelectedPeriodo(deepLinkPeriodo);
      if (deepLinkQuincena) setSelectedQuincena(deepLinkQuincena);
      setSelectedEmployee(emp);
      onDeepLinkConsumed?.();
    }
  }, [deepLinkEmployeeId, loading, employees]); // eslint-disable-line

  const activeEmployees = employees.filter(e => e.estado === 'activo');

  const filteredEmployees = activeEmployees.filter(emp =>
    `${emp.nombre} ${emp.apellido}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.employeeId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSave = (data) => {
    console.log('✅ Ingresos guardados:', data);
    setSelectedEmployee(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">Cargando empleados...</p>
        </div>
      </div>
    );
  }

  if (selectedEmployee) {
    return (
      <VariableIncomesModule
        employeeId={selectedEmployee.id}
        employeeName={`${selectedEmployee.nombre} ${selectedEmployee.apellido}`}
        employeeIdDisplay={selectedEmployee.employeeId}
        cargo={selectedEmployee.cargo}
        periodo={selectedPeriodo}
        quincena={selectedQuincena}
        salarioBase={parseFloat(selectedEmployee.salario || 0)}
        onSave={handleSave}
        onCancel={() => setSelectedEmployee(null)}
      />
    );
  }

  return (
    <div className="space-y-4">

      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
          {filteredEmployees.length} de {activeEmployees.length}
        </span>

        {/* Period picker */}
        <div className="flex items-center gap-1.5">
          <Calendar size={14} className="text-gray-400" />
          <input
            type="month"
            value={selectedPeriodo}
            onChange={(e) => setSelectedPeriodo(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
          />
        </div>

        {/* Quincena toggle */}
        <div className="flex border border-gray-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setSelectedQuincena('Q1')}
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
              selectedQuincena === 'Q1'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-500 hover:bg-gray-50'
            }`}
          >
            1ra Quincena
          </button>
          <button
            onClick={() => setSelectedQuincena('Q2')}
            className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-gray-200 ${
              selectedQuincena === 'Q2'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-500 hover:bg-gray-50'
            }`}
          >
            2da Quincena
          </button>
        </div>

        {/* Search */}
        <div className="relative flex-1 max-w-xs ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar empleado..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
            >
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      {/* ── Employee Table ───────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-blue-600 text-white">
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Empleado</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Cargo</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider hidden lg:table-cell">Departamento</th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Salario Base</th>
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Acción</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredEmployees.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-400">
                  {searchTerm ? 'No se encontraron empleados' : 'No hay empleados activos'}
                </td>
              </tr>
            ) : (
              filteredEmployees.map((emp) => (
                <tr
                  key={emp.id}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setSelectedEmployee(emp)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {emp.nombre?.[0]}{emp.apellido?.[0]}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {emp.nombre} {emp.apellido}
                        </p>
                        <p className="text-xs text-gray-400">{emp.employeeId}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="text-sm text-gray-700">{emp.cargo || '—'}</span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <span className="text-sm text-gray-700">{emp.departamento || '—'}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-semibold text-gray-900">
                      RD$ {fmt(emp.salario)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                      <TrendingUp size={12} />
                      Gestionar
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {filteredEmployees.length > 0 && (
            <tfoot>
              <tr className="bg-gray-50 border-t border-gray-200">
                <td colSpan={5} className="px-4 py-2 text-xs text-gray-400">
                  {filteredEmployees.length} empleado{filteredEmployees.length !== 1 ? 's' : ''} activo{filteredEmployees.length !== 1 ? 's' : ''} · Período: {selectedPeriodo} · {selectedQuincena === 'Q1' ? '1ra quincena (1–15)' : '2da quincena (16–fin)'}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

    </div>
  );
}
